import { Controller } from "@hotwired/stimulus";
import { Collapse } from "bootstrap";

/**
 * Nested Sortable Controller
 *
 * Supports drag-and-drop reordering of items at the same level:
 * - Root-level items can be reordered among each other
 * - Child items within a container can be reordered among siblings
 * - Items cannot be dragged between levels; use "Add child" buttons instead
 *
 * Reordering is done with CSS `order` on flex containers, NOT by moving DOM
 * nodes. Moving a node that contains a live editor (Trix is a custom element,
 * TomSelect a JS-managed widget, Bootstrap collapse a stateful instance) tears
 * that widget's state down and rebuilds it. Keeping every node stationary and
 * only changing its visual `order` sidesteps all of that: the editors are never
 * disturbed by a reorder. The hidden nu_position field tracks the logical order
 * for submission.
 *
 * On page load, items with a nu_parent_id are moved into their parent's
 * children container so the visual hierarchy matches the data. This is the one
 * structural DOM move (one-time, at load) and it preserves Trix/select state.
 */
export default class extends Controller {
  static targets = [
    "item", // each sortable row/item
    "handle", // drag handle inside an item
    "positionInput", // position input inside an item
    "parentInput", // parent_id input inside an item
    "destroyInput", // _destroy input inside an item
    "links", // container with add links to act as anchor
    "childrenContainer", // container for child items within a parent
  ];

  static values = {
    // Duration (ms) of the FLIP slide when items reorder during a drag.
    sortAnimMs: { type: Number, default: 350 },
  };

  // CSS `order` assigned to an item that hasn't been ordered yet, so it sorts
  // after positioned items (just below the pinned "Add section" controls).
  static ORDER_UNSET = 999_999;

  connect() {
    this.dragged = null;
    this.isConnected = true;
    this.initialized = false;
    this.prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    this.boundTurboLoadHandler = this.handleTurboLoad.bind(this);
    document.addEventListener("turbo:load", this.boundTurboLoadHandler);

    this.tryInit();
  }

  handleTurboLoad() {
    if (this.isConnected && !this.initialized) {
      this.tryInit();
    }
  }

  tryInit() {
    requestAnimationFrame(() => {
      if (!this.isConnected) return;

      if (this.itemTargets.length === 0) {
        requestAnimationFrame(() => this.tryInit());
        return;
      }

      this.performInit();
    });
  }

  performInit() {
    if (this.initialized || !this.isConnected) return;
    this.initialized = true;

    // Move children into their parent containers based on nu_parent_id values
    this.organizeChildrenByParent();

    this.initItems();
    this.updatePositions();
    this.bindSubmit();

    // Watch only childList/subtree: new sections added by forms--nested-attrs
    // need drag handles and an order. We deliberately do NOT watch attributes,
    // so our own `style.order` / value writes don't feed back into this.
    this.observerRAF = null;
    this.observer = new MutationObserver(() => {
      // Debounce: batch rapid mutations into a single callback per frame
      if (this.observerRAF) cancelAnimationFrame(this.observerRAF);
      this.observerRAF = requestAnimationFrame(() => {
        this.observerRAF = null;
        this.initItems();
        this.updatePositions();
      });
    });
    this.startObserver();
  }

  // Re-run updatePositions right before the form serializes. Drag updates the
  // order live, but a brand-new section added and submitted in the same tick
  // could otherwise post a blank nu_position; this guarantees every visible
  // section has a sequential position at submit time.
  bindSubmit() {
    const form = this.element.closest("form");
    if (!form || form.dataset.nestedSortableSubmitBound) return;
    form.addEventListener("submit", () => this.updatePositions());
    form.dataset.nestedSortableSubmitBound = "true";
  }

  // On page load, move items with nu_parent_id into their parent's children
  // container. Rails renders all sections flat; this visually nests existing
  // children. This is a real DOM move, so it preserves Trix/select state.
  organizeChildrenByParent() {
    this.itemTargets.forEach((item) => {
      const parentInput = item.querySelector(
        this.targetSelector("parentInput"),
      );
      const parentId = parentInput?.value;

      if (parentId) {
        const parentContainer = this.element.querySelector(
          `[data-parent-section-id="${parentId}"]`,
        );
        if (parentContainer && item.parentElement !== parentContainer) {
          // Save select values before the DOM move. Moving a subtree triggers
          // the basic-select controller's disconnect/reconnect, which loses the
          // TomSelect value; restore it on the native <select> afterwards.
          const savedSelects = this.saveSelectValues(item);

          // Detach Trix editors before the move to prevent toolbar loss.
          const detachedTrix = this.detachTrixEditors(item);

          const addLink = parentContainer.querySelector(
            ":scope > .children-add-link",
          );
          if (addLink) {
            parentContainer.insertBefore(item, addLink);
          } else {
            parentContainer.appendChild(item);
          }

          this.reattachTrixEditors(detachedTrix);
          this.restoreSelectValues(savedSelects);

          item.classList.add("nested-child");
        }
      }
    });
  }

  startObserver() {
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
    });
  }

  // --- Add child ---

  /**
   * Adds a new child item inside a parent's children container.
   * Stamps a new record from the page-level template, sets its nu_parent_id,
   * applies child styling, and inserts it into the container.
   */
  addChild(event) {
    event.preventDefault();

    const item = event.target.closest(this.targetSelector("item"));
    if (!item) return;

    const pid = this.parentId(item);
    if (!pid) return;

    // Find the page-level template (used by forms--nested-attrs)
    const template = this.element.querySelector(
      '[data-forms--nested-attrs-target="template"]',
    );
    if (!template) return;

    const newIndex = new Date().getTime();
    const content = template.innerHTML.replace(/NEW_RECORD/g, newIndex);

    const wrapper = document.createElement("div");
    wrapper.innerHTML = content;
    const newItem = wrapper.firstElementChild;
    if (!newItem) return;

    // Set the parent_id on the new child
    const parentInput = newItem.querySelector(
      this.targetSelector("parentInput"),
    );
    if (parentInput) parentInput.value = pid;

    // Add child visual styling
    newItem.classList.add("nested-child");

    // Add child badge after the label text in the accordion header
    const header = newItem.querySelector(".accordion-button");
    if (header) {
      const badge = document.createElement("span");
      badge.className = "badge bg-secondary ms-2";
      badge.textContent = "Child";
      // Insert after the first text node or element
      header.appendChild(badge);
    }

    // Hide the children container on the new child (single-level nesting)
    const childContainer = newItem.querySelector(".children-container");
    if (childContainer) childContainer.classList.add("d-none");

    // Disable accordion parent behaviour so children can be opened independently
    const collapse = newItem.querySelector(".accordion-collapse");
    if (collapse) collapse.removeAttribute("data-bs-parent");

    // Insert into the parent's children container, before the add button
    const container = item.querySelector(
      this.targetSelector("childrenContainer"),
    );
    if (!container) return;

    const addButton = container.querySelector(":scope > .children-add-link");
    if (addButton) {
      container.insertBefore(newItem, addButton);
    } else {
      container.appendChild(newItem);
    }
  }

  /**
   * Returns the persisted parent ID for the given item.
   * Children can only be added to persisted items, so the
   * data-parent-section-id attribute is always set from the record's id.
   */
  parentId(item) {
    const container = item.querySelector(
      this.targetSelector("childrenContainer"),
    );
    return container?.dataset.parentSectionId || null;
  }

  // --- DOM preservation helpers (used by the load-time nesting move) ---

  saveSelectValues(root) {
    const entries = [];
    root.querySelectorAll("select").forEach((select) => {
      const value = select.tomselect
        ? select.tomselect.getValue()
        : select.value;
      entries.push({ select, value });
    });
    return entries;
  }

  restoreSelectValues(entries) {
    entries.forEach(({ select, value }) => {
      select.value = value;
    });
  }

  detachTrixEditors(root) {
    const entries = [];
    root.querySelectorAll("trix-editor").forEach((editor) => {
      const parent = editor.parentNode;
      if (!parent) return;

      const toolbar = editor.previousElementSibling;
      const isToolbar =
        toolbar && toolbar.tagName.toLowerCase() === "trix-toolbar";

      const placeholder = document.createComment("trix-placeholder");
      parent.insertBefore(placeholder, editor);

      if (isToolbar) toolbar.remove();
      editor.remove();

      entries.push({
        editor,
        toolbar: isToolbar ? toolbar : null,
        placeholder,
      });
    });
    return entries;
  }

  reattachTrixEditors(entries) {
    entries.forEach(({ editor, toolbar, placeholder }) => {
      const parent = placeholder.parentNode;
      if (!parent) return;

      if (toolbar) parent.insertBefore(toolbar, placeholder);
      parent.insertBefore(editor, placeholder);
      placeholder.remove();
    });
  }

  // --- Drag and drop (same-level reordering via CSS order) ---

  initItems() {
    this.itemTargets.forEach((item) => {
      item.removeAttribute("draggable");

      const handle = item.querySelector(this.targetSelector("handle"));
      if (!handle) return;
      if (handle.dataset.nestedSortableInitialized === "true") return;

      handle.setAttribute("draggable", "true");
      handle.addEventListener("dragstart", this.onDragStart);
      handle.addEventListener("dragend", this.onDragEnd);
      handle.dataset.nestedSortableInitialized = "true";
    });

    if (!this.element.dataset.nestedSortableContainerInitialized) {
      this.element.addEventListener("dragover", this.onDragOver);
      this.element.addEventListener("drop", this.onDrop);
      this.element.dataset.nestedSortableContainerInitialized = "true";
    }

    // Allow sibling reordering within children containers
    this.childrenContainerTargets.forEach((container) => {
      if (!container.dataset.nestedSortableChildInitialized) {
        container.addEventListener("dragover", this.onChildContainerDragOver);
        container.addEventListener("drop", this.onDrop);
        container.dataset.nestedSortableChildInitialized = "true";
      }
    });
  }

  get rootItems() {
    return this.itemTargets.filter((item) => {
      const isInsideChildContainer = item.parentElement?.matches(
        this.targetSelector("childrenContainer"),
      );
      return !isInsideChildContainer;
    });
  }

  dragContext(item) {
    const parentContainer = item.parentElement?.closest(
      this.targetSelector("childrenContainer"),
    );
    return parentContainer || this.element;
  }

  // The non-destroyed items belonging to a container (root or children),
  // in their current visual order (by CSS order).
  orderedItems(container) {
    const items =
      container === this.element
        ? this.rootItems
        : Array.from(
            container.querySelectorAll(
              `:scope > ${this.targetSelector("item")}`,
            ),
          );
    return items
      .filter((item) => !this.isDestroyed(item))
      .sort((a, b) => this.orderOf(a) - this.orderOf(b));
  }

  orderOf(item) {
    const o = parseFloat(item.style.order);
    return Number.isFinite(o) ? o : this.constructor.ORDER_UNSET;
  }

  onDragStart = (e) => {
    const handle = e.currentTarget;
    const item = handle.closest(this.targetSelector("item"));
    if (!item) return;

    // Pause the observer to avoid reacting to anything mid-drag
    if (this.observerRAF) cancelAnimationFrame(this.observerRAF);
    this.observer.disconnect();

    this.dragged = item;
    this.dragContainer = this.dragContext(item);
    this.dragged.classList.add("dragging");
    this.dragRAF = null;
    this.animatedEls = new Set();

    // Collapse the dragged section through Bootstrap so its state stays in sync
    // (button gets `.collapsed`, aria-expanded flips, instance knows it's hidden).
    this.collapseSection(item);

    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", "");
  };

  collapseSection(item) {
    const collapse = item.querySelector(".accordion-collapse");
    if (collapse && collapse.classList.contains("show")) {
      Collapse.getOrCreateInstance(collapse, { toggle: false }).hide();
    }
  }

  onDragEnd = () => {
    if (this.dragRAF) {
      cancelAnimationFrame(this.dragRAF);
      this.dragRAF = null;
    }

    if (this.dragged) {
      this.dragged.classList.remove("dragging");
      this.dragged = null;
      this.dragContainer = null;
    }
    this.clearFlipStyles();
    this.updatePositions();

    // Reconnect the observer now that drag is complete
    this.startObserver();
  };

  onDragOver = (e) => {
    if (!this.dragged) return;

    // Don't handle if we're over a children container
    const overChildContainer = e.target.closest(
      this.targetSelector("childrenContainer"),
    );
    if (overChildContainer) return;

    // Only root items can be reordered at root level
    if (this.dragContainer !== this.element) return;

    e.preventDefault();
    this.scheduleReorder(this.element, e.clientY);
  };

  // Handle reordering of children within a container
  onChildContainerDragOver = (e) => {
    if (!this.dragged) return;

    const container = e.currentTarget;

    // Only allow reordering if dragged item belongs to this container
    if (this.dragContainer !== container) return;

    e.preventDefault();
    e.stopPropagation();
    this.scheduleReorder(container, e.clientY);
  };

  // Throttle reorder computation to once per animation frame.
  scheduleReorder(container, y) {
    if (this.dragRAF) return;
    this.dragRAF = requestAnimationFrame(() => {
      this.dragRAF = null;
      if (!this.dragged) return;
      this.reorderTo(container, y);
    });
  }

  // Recompute the visual sequence for the pointer position and re-assign CSS
  // order. No DOM node is moved — only `style.order` changes.
  reorderTo(container, y) {
    const seq = this.orderedItems(container);
    const without = seq.filter((el) => el !== this.dragged);

    const afterElement = this.getDragAfterElement(without, y);
    const idx = afterElement ? without.indexOf(afterElement) : without.length;
    const newSeq = [
      ...without.slice(0, idx),
      this.dragged,
      ...without.slice(idx),
    ];

    // Skip if nothing changed
    if (newSeq.every((el, i) => el === seq[i])) return;

    // FLIP-animate the shifted siblings; the dragged item snaps to its new slot.
    this.flipAnimate(without, () => {
      newSeq.forEach((el, i) => {
        el.style.order = i;
      });
    });
  }

  onDrop = (e) => {
    e.preventDefault();
    this.updatePositions();
  };

  // Slide the given sibling items from their old positions to their new ones
  // using the FLIP technique, so a reorder visibly "opens a gap". The dragged
  // item is excluded by the caller: it snaps straight to its new slot.
  flipAnimate(siblings, doMove) {
    if (this.prefersReducedMotion) {
      doMove();
      return;
    }

    // Snap any still-animating siblings to their current layout position before
    // recording FIRST, so a mid-transition read doesn't produce a stale delta.
    this.clearFlipStyles();
    void this.element.offsetHeight; // commit the snap before reading

    // FIRST: record each sibling's settled position.
    const firstTops = new Map();
    siblings.forEach((el) => firstTops.set(el, el.getBoundingClientRect().top));

    // MOVE: re-assign CSS order.
    doMove();

    // INVERT + PLAY: offset each shifted sibling back to where it was, then
    // transition the offset away so it slides into place.
    const lastTops = new Map();
    siblings.forEach((el) => lastTops.set(el, el.getBoundingClientRect().top));

    siblings.forEach((el) => {
      const delta = firstTops.get(el) - lastTops.get(el);
      if (!delta) return;
      el.style.transition = "none";
      el.style.transform = `translateY(${delta}px)`;
      this.animatedEls.add(el);
    });

    // Single reflow commits every transform before the transitions start.
    void this.element.offsetHeight;

    // Start all transitions in one pass.
    this.animatedEls.forEach((el) => {
      el.style.transition = `transform ${this.sortAnimMsValue}ms ease`;
      el.style.transform = "";
    });
  }

  // Remove any inline transform/transition left by flipAnimate.
  clearFlipStyles() {
    if (!this.animatedEls) return;
    this.animatedEls.forEach((el) => {
      el.style.transition = "";
      el.style.transform = "";
    });
    this.animatedEls.clear();
  }

  // The candidate (already excludes the dragged item) the pointer sits above.
  getDragAfterElement(candidates, y) {
    let closest = { offset: Number.NEGATIVE_INFINITY, element: null };
    candidates.forEach((child) => {
      const box = child.getBoundingClientRect();
      const offset = y - (box.top + box.height / 2);
      if (offset < 0 && offset > closest.offset) {
        closest = { offset, element: child };
      }
    });
    return closest.element;
  }

  // Normalize CSS order to a contiguous 0..n range and write the 1-based
  // nu_position for every visible section in each container.
  updatePositions() {
    this.normalizeContainer(this.element);
    this.childrenContainerTargets.forEach((container) =>
      this.normalizeContainer(container),
    );
  }

  normalizeContainer(container) {
    const seq = this.orderedItems(container);
    seq.forEach((item, idx) => {
      item.style.order = idx;
      const positionInput = item.querySelector(
        this.targetSelector("positionInput"),
      );
      if (positionInput) {
        positionInput.value = idx + 1;
      }
    });
  }

  // --- Common helpers ---

  isDestroyed(item) {
    const destroyInput = item.querySelector(
      this.targetSelector("destroyInput"),
    );
    const isDestroyed = destroyInput && destroyInput.value === "1";
    const isHidden = item.style && item.style.display === "none";
    return isDestroyed || isHidden;
  }

  targetSelector(name) {
    return `[data-${this.identifier}-target="${name}"]`;
  }

  disconnect() {
    this.isConnected = false;
    if (this.boundTurboLoadHandler) {
      document.removeEventListener("turbo:load", this.boundTurboLoadHandler);
    }
    if (this.dragRAF) cancelAnimationFrame(this.dragRAF);
    if (this.observerRAF) cancelAnimationFrame(this.observerRAF);
    if (this.observer) this.observer.disconnect();
  }
}
